import {
  Box,
  Button,
  Grid,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react';
import { inquiryApi, SupplierInquiry } from '@texas/api/endpoints/inquiryApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { Icons } from '@texas/components/shared/Icons';
import { Locale } from '@texas/i18n/types';
import {
  formatDate,
  DateFormat,
  toDateInputValue,
  toISOString,
} from '@texas/utils/helpers/dateHelper';
import { useTranslation } from 'react-i18next';
import { inquiryEvents } from '../events';
import { InquiryAcceptRate } from '../InquiryAcceptRate';

interface Props {
  data: SupplierInquiry;
}

export function InquiryStatusSection({ data }: Props) {
  const { t } = useTranslation();
  const isArchived = data.archived != null;
  const isAccepted = data.acceptedDate != null;
  const { request: updateSentRequest, loading: updatingSent } = useApiRequest(
    inquiryApi.updateSent,
  );
  const { request: archiveRequest, loading: archiving } = useApiRequest(
    inquiryApi.archiveInquiry,
  );
  const { request: restoreRequest, loading: restoring } = useApiRequest(
    inquiryApi.restoreInquiry,
  );

  return (
    <Box
      borderTopWidth={{ base: '1px', '2xl': 0 }}
      borderLeftWidth={{ base: 0, '2xl': '1px' }}
      borderStyle="solid"
      borderColor="inquiryOverview.softBorder"
      pl={{ base: 0, '2xl': 8 }}
      pt={{ base: 5, '2xl': 0 }}
      gridColumn={{ lg: '1 / -1', '2xl': 'auto' }}
      mt={{ lg: 5, '2xl': 0 }}
    >
      <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" mb={4}>
        {t('inquiry.status')}
      </Text>
      <Grid
        templateColumns="1fr auto"
        gap={3}
        alignItems="center"
        fontSize="sm"
      >
        <Text color="inquiryOverview.mutedText">{t('inquiry.sent')}</Text>
        <Popover placement="bottom-end">
          <PopoverTrigger>
            <Button
              size="sm"
              variant="link"
              color="inquiryOverview.text"
              fontWeight="semibold"
              isDisabled={updatingSent || isAccepted}
            >
              {data.sent
                ? formatDate(Locale.En, data.sent, DateFormat.Date)
                : t('inquiry.notSent')}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            w="220px"
            bg="inquiryOverview.surface"
            borderColor="inquiryOverview.softBorder"
          >
            <PopoverArrow />
            <PopoverBody>
              <Text fontSize="xs" mb={2}>
                {t('inquiry.editSentDate')}
              </Text>
              <Input
                variant="texas"
                size="sm"
                type="date"
                value={toDateInputValue(data.sent)}
                onChange={async (event) => {
                  const res = await updateSentRequest(data.id, {
                    sent: toISOString(event.target.value),
                  });
                  if (!res.hasError) inquiryEvents.onUpdated.dispatch();
                }}
              />
            </PopoverBody>
          </PopoverContent>
        </Popover>
        <Text color="inquiryOverview.mutedText">{t('inquiry.acceptRate')}</Text>
        <InquiryAcceptRate
          acceptedPriceCount={data.acceptedPriceCount}
          priceCount={data.priceCount}
          isAccepted={isAccepted}
        />
      </Grid>
      <Box
        borderTop="1px solid"
        borderColor="inquiryOverview.softBorder"
        mt={5}
        pt={4}
      >
        {isArchived ? (
          <Button
            size="sm"
            variant="link"
            leftIcon={<Icons.ArchiveOutline boxSize={4} />}
            isLoading={restoring}
            onClick={async () => {
              const res = await restoreRequest(data.id);
              if (!res.hasError) inquiryEvents.onUpdated.dispatch();
            }}
          >
            {t('general.restore')}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="link"
            color="texas.burntSienna.500"
            leftIcon={<Icons.Archive boxSize={4} />}
            isLoading={archiving}
            onClick={async () => {
              const res = await archiveRequest(data.id);
              if (!res.hasError) inquiryEvents.onUpdated.dispatch();
            }}
          >
            {t('general.archive')}
          </Button>
        )}
      </Box>
    </Box>
  );
}
